import React from 'react';
import { useForm, SubmitHandler } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress,
  styled,
  useTheme,
  useMediaQuery,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { useMutation } from '@apollo/client';
import { INVITE_USER_TO_COMPANY } from 'src/utils/gql/mutations/companyInvite';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';
import { InviteUserInput } from 'src/utils/gql/types';
import { useIsSuperAdmin, useIsCompanyAdmin } from 'src/hooks/useIsAdmin';

const inviteUserSchema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  firstName: yup.string().required('First name is required'),
  lastName: yup.string().required('Last name is required'),
  role: yup.string().required('Role is required'),
});

type InviteUserFormValues = InviteUserInput;

// Styled components per il form responsive
const FormContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  maxWidth: '500px',
  width: '100%',

  [theme.breakpoints.down('sm')]: {
    gap: '12px',
  },
}));

const FormTitle = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 600,
  marginBottom: '16px',

  [theme.breakpoints.down('sm')]: {
    fontSize: '18px',
    marginBottom: '12px',
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
  },

  [theme.breakpoints.down('sm')]: {
    '& .MuiOutlinedInput-root': {
      fontSize: '14px',
    },
  },
}));

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: '16px',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '16px',
  fontWeight: 600,

  [theme.breakpoints.down('sm')]: {
    marginTop: '12px',
    padding: '10px 20px',
    fontSize: '14px',
  },
}));

interface InviteUserFormProps {
  onSuccess?: () => void;
  disabled?: boolean;
}

export const InviteUserForm: React.FC<InviteUserFormProps> = ({
  onSuccess,
  disabled = false,
}) => {
  const snackbar = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isSuperAdmin = useIsSuperAdmin();
  const isCompanyAdmin = useIsCompanyAdmin();

  const formMethods = useForm<InviteUserFormValues>({
    resolver: yupResolver(inviteUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      role: 'Customer', // Default per tutti gli utenti
    },
  });

  const [inviteUser, { loading }] = useMutation(INVITE_USER_TO_COMPANY, {
    onCompleted: () => {
      snackbar('Invite sent successfully!', 'success');
      formMethods.reset();
      onSuccess?.();
    },
    onError: (error) => {
      snackbar(error.message);
    },
  });

  const onSubmit: SubmitHandler<InviteUserFormValues> = (data) => {
    inviteUser({
      variables: {
        inviteData: data,
      },
    });
  };

  return (
    <FormContainer
      component="form"
      onSubmit={formMethods.handleSubmit(onSubmit)}
    >
      <FormTitle variant="h6">Invite new user</FormTitle>

      <StyledTextField
        fullWidth
        label="Email"
        {...formMethods.register('email')}
        error={!!formMethods.formState.errors.email}
        helperText={formMethods.formState.errors.email?.message}
        size={isMobile ? 'small' : 'medium'}
      />

      <StyledTextField
        fullWidth
        label="First Name"
        {...formMethods.register('firstName')}
        error={!!formMethods.formState.errors.firstName}
        helperText={formMethods.formState.errors.firstName?.message}
        size={isMobile ? 'small' : 'medium'}
      />

      <StyledTextField
        fullWidth
        label="Last Name"
        {...formMethods.register('lastName')}
        error={!!formMethods.formState.errors.lastName}
        helperText={formMethods.formState.errors.lastName?.message}
        size={isMobile ? 'small' : 'medium'}
      />

      <FormControl
        fullWidth
        sx={{
          '& .MuiInputLabel-root': {
            marginBottom: theme.spacing(3),
            fontSize: '0.875rem',
            fontWeight: 500,
            color: 'text.secondary',
          },
          '& .MuiOutlinedInput-root': {
            borderRadius: '6px',
          },
        }}
      >
        <InputLabel sx={{ mb: 5 }}>Permission Level</InputLabel>
        <Select
          value={formMethods.watch('role')}
          onChange={(e) => formMethods.setValue('role', e.target.value)}
          size={isMobile ? 'small' : 'medium'}
          sx={{
            borderRadius: '6px',
            '& .MuiSelect-select': {
              padding: {
                xs: theme.spacing(2, 2),
                sm: theme.spacing(2.5, 2),
              },
              fontSize: {
                xs: '0.875rem',
                sm: '1rem',
              },
            },
          }}
        >
          <MenuItem value="Customer">User</MenuItem>
          {/* Company Admin può invitare altri Company Admin */}
          {(isSuperAdmin || isCompanyAdmin) && (
            <MenuItem value="CompanyAdmin">Company Admin</MenuItem>
          )}
          {/* Solo Admin può invitare Admin */}
          {isSuperAdmin && <MenuItem value="Admin">Admin</MenuItem>}
        </Select>
      </FormControl>

      <SubmitButton
        type="submit"
        variant="contained"
        fullWidth
        disabled={loading || disabled}
      >
        {loading ? (
          <CircularProgress size={isMobile ? 20 : 24} />
        ) : disabled ? (
          'Plan Limit Reached'
        ) : (
          'Send Invite'
        )}
      </SubmitButton>
    </FormContainer>
  );
};
